import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Grid, Button, Avatar, TextField, MenuItem, Table, TableHead, TableRow, TableCell, TableBody, Chip, IconButton, Snackbar, Alert, Tabs, Tab } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import api from '../../api';

const EmployeeProfile = () => {
  const empId = localStorage.getItem('emp_id');
  const [tabValue, setTabValue] = useState(0);
  const [profile, setProfile] = useState({});
  const [employees, setEmployees] = useState([]); 
  const [docCategories, setDocCategories] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [docFile, setDocFile] = useState(null);
  const [docType, setDocType] = useState('');
  
  const [photoStamp, setPhotoStamp] = useState(Date.now());
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', type: 'success' });
  const showMsg = (msg, type = 'success') => setSnackbar({ open: true, msg, type });

  useEffect(() => {
    fetchProfileData();
    fetchDocuments();
    api.get('/settings/document_categories').then(res => setDocCategories(res.data)).catch(() => {});
    api.get('/employees').then(res => setEmployees(res.data)).catch(() => {});
  }, []);

  const fetchProfileData = async () => {
    try {
      const res = await api.get(`/employees/${empId}`);
      setProfile(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchDocuments = async () => {
    try {
      const res = await api.get(`/employees/${empId}/documents`);
      setDocuments(res.data);
    } catch (err) { console.error(err); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      await api.post(`/employees/${empId}/profile-picture`, formData);
      showMsg("Profile Picture Updated!");
      setPhotoStamp(Date.now());
    } catch (err) { showMsg("Failed to update picture", "error"); }
  };

  const handleUpload = async () => {
    if (!docFile || !docType) return showMsg("Select category and file", "error");
    const formData = new FormData();
    formData.append("file", docFile);
    formData.append("document_type", docType);

    try {
      await api.post(`/employees/${empId}/documents`, formData);
      showMsg("Document Uploaded!");
      setDocFile(null); setDocType('');
      fetchDocuments();
    } catch (err) { showMsg("Upload Failed", "error"); }
  };

  // Helper to map IDs to Names for Approvers
  const getEmpName = (id) => {
    if (!id) return '--';
    const emp = employees.find(e => e.emp_id === id);
    return emp ? `${emp.first_name} ${emp.last_name}` : id;
  };

  return (
    <Box sx={{ mt: 2, maxWidth: 1000, mx: 'auto' }}>
      <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 4, bgcolor: '#f8f9fa' }}>
        <Box sx={{ position: 'relative' }}>
          <Avatar 
            src={`http://${window.location.hostname}:8000/api/v1/employees/${empId}/profile-picture?t=${photoStamp}`} 
            sx={{ width: 120, height: 120, border: '4px solid white', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }} 
          />
          <IconButton component="label" sx={{ position: 'absolute', bottom: 0, right: 0, bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}>
            <PhotoCameraIcon fontSize="small" />
            <input hidden accept="image/*" type="file" onChange={handlePhotoUpload} />
          </IconButton>
        </Box>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#333' }}>{profile.first_name} {profile.last_name}</Typography>
          <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 1 }}>{profile.emp_id} � {profile.status}</Typography>
          <Chip label={profile.employee_level || 'Employee'} color="primary" variant="outlined" size="small" />
        </Box>
      </Paper>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, val) => setTabValue(val)}>
          <Tab label="Profile Details" sx={{ fontWeight: 'bold' }} />
          <Tab label="My Documents" sx={{ fontWeight: 'bold' }} />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Section 1: Basic Information */}
          <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>Basic Information</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}><Typography variant="subtitle2" color="textSecondary">Employee ID</Typography><Typography variant="body1" sx={{ fontWeight: '500' }}>{profile.emp_id || '--'}</Typography></Grid>
              <Grid item xs={12} sm={4}><Typography variant="subtitle2" color="textSecondary">First Name</Typography><Typography variant="body1" sx={{ fontWeight: '500' }}>{profile.first_name || '--'}</Typography></Grid>
              <Grid item xs={12} sm={4}><Typography variant="subtitle2" color="textSecondary">Last Name</Typography><Typography variant="body1" sx={{ fontWeight: '500' }}>{profile.last_name || '--'}</Typography></Grid>
              <Grid item xs={12} sm={4}><Typography variant="subtitle2" color="textSecondary">Email</Typography><Typography variant="body1" sx={{ fontWeight: '500' }}>{profile.email || '--'}</Typography></Grid>
              <Grid item xs={12} sm={4}><Typography variant="subtitle2" color="textSecondary">Phone</Typography><Typography variant="body1" sx={{ fontWeight: '500' }}>{profile.phone || '--'}</Typography></Grid>
              <Grid item xs={12} sm={4}><Typography variant="subtitle2" color="textSecondary">Gender</Typography><Typography variant="body1" sx={{ fontWeight: '500' }}>{profile.gender || '--'}</Typography></Grid>
              <Grid item xs={12} sm={4}><Typography variant="subtitle2" color="textSecondary">Date of Birth</Typography><Typography variant="body1" sx={{ fontWeight: '500' }}>{profile.date_of_birth || '--'}</Typography></Grid>
            </Grid>
          </Paper>

          {/* Section 2: Job & Organization */}
          <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>Job & Organization</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}><Typography variant="subtitle2" color="textSecondary">Department ID</Typography><Typography variant="body1" sx={{ fontWeight: '500' }}>{profile.department_id || '--'}</Typography></Grid>
              <Grid item xs={12} sm={4}><Typography variant="subtitle2" color="textSecondary">Designation ID</Typography><Typography variant="body1" sx={{ fontWeight: '500' }}>{profile.designation_id || '--'}</Typography></Grid>
              <Grid item xs={12} sm={4}><Typography variant="subtitle2" color="textSecondary">Date of Joining</Typography><Typography variant="body1" sx={{ fontWeight: '500' }}>{profile.date_of_joining || '--'}</Typography></Grid>
              <Grid item xs={12} sm={4}><Typography variant="subtitle2" color="textSecondary">Status</Typography><Typography variant="body1" sx={{ fontWeight: '500' }}>{profile.status || '--'}</Typography></Grid>
              <Grid item xs={12} sm={4}><Typography variant="subtitle2" color="textSecondary">Employee Level</Typography><Typography variant="body1" sx={{ fontWeight: '500' }}>{profile.employee_level || '--'}</Typography></Grid>
              <Grid item xs={12} sm={4}><Typography variant="subtitle2" color="textSecondary">L1 Leave Approver</Typography><Typography variant="body1" sx={{ fontWeight: '500' }}>{getEmpName(profile.leave_approver_l1)}</Typography></Grid>
              <Grid item xs={12} sm={4}><Typography variant="subtitle2" color="textSecondary">L2 Leave Approver</Typography><Typography variant="body1" sx={{ fontWeight: '500' }}>{getEmpName(profile.leave_approver_l2)}</Typography></Grid>
            </Grid>
          </Paper>

          {/* Section 3: Personal & Emergency Details */}
          <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>Personal & Emergency Details</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}><Typography variant="subtitle2" color="textSecondary">Blood Group</Typography><Typography variant="body1" sx={{ fontWeight: '500' }}>{profile.blood_group || '--'}</Typography></Grid>
              <Grid item xs={12} sm={4}><Typography variant="subtitle2" color="textSecondary">Marital Status</Typography><Typography variant="body1" sx={{ fontWeight: '500' }}>{profile.marital_status || '--'}</Typography></Grid>
              <Grid item xs={12} sm={12}><Typography variant="subtitle2" color="textSecondary">Address</Typography><Typography variant="body1" sx={{ fontWeight: '500' }}>{profile.address || '--'}</Typography></Grid>
              <Grid item xs={12} sm={4}><Typography variant="subtitle2" color="textSecondary">Emergency Contact Name</Typography><Typography variant="body1" sx={{ fontWeight: '500' }}>{profile.emergency_contact_name || '--'}</Typography></Grid>
              <Grid item xs={12} sm={4}><Typography variant="subtitle2" color="textSecondary">Emergency Contact Phone</Typography><Typography variant="body1" sx={{ fontWeight: '500' }}>{profile.emergency_contact_phone || '--'}</Typography></Grid>
            </Grid>
          </Paper>
        </Box>
      )}

      {tabValue === 1 && (
        <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 4, alignItems: 'center', bgcolor: '#f5f5f5', p: 2, borderRadius: 2 }}>
            <TextField select size="small" label="Document Category" value={docType} onChange={e => setDocType(e.target.value)} sx={{ minWidth: 200 }}>
              {docCategories.map(cat => <MenuItem key={cat.id} value={cat.name}>{cat.name}</MenuItem>)}
            </TextField>
            <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
              {docFile ? docFile.name : "Choose File"}
              <input type="file" hidden onChange={e => setDocFile(e.target.files[0])} />
            </Button>
            <Button variant="contained" onClick={handleUpload}>Upload Document</Button>
          </Box>

          <Typography variant="h6" gutterBottom>Uploaded Documents</Typography>
          <Table size="small">
            <TableHead sx={{ bgcolor: '#e3f2fd' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Uploaded On</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.length === 0 ? <TableRow><TableCell colSpan={4} align="center">No documents in vault.</TableCell></TableRow> :
                documents.map(doc => (
                  <TableRow key={doc.id} hover>
                    <TableCell>{doc.document_type}</TableCell>
                    <TableCell>{new Date(doc.upload_date || new Date()).toLocaleDateString()}</TableCell>
                    <TableCell><Chip size="small" label={doc.status} color={doc.status === 'APPROVED' ? 'success' : (doc.status === 'REJECTED' ? 'error' : 'warning')} /></TableCell>
                    <TableCell align="right">
                      <Button size="small" startIcon={<DownloadIcon />} onClick={() => window.open(`http://${window.location.hostname}:8000/api/v1/employees/documents/${doc.id}/download`, '_blank')}>Download</Button>
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
          </Table>
        </Paper>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.type} variant="filled" sx={{ width: '100%' }}>{snackbar.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default EmployeeProfile;